import React from 'react';
import { X } from 'lucide-react';
import Text from './Text';
import IconButton from './IconButton';
import useClickOutside from '../../hooks/useClickOutside';
import useDialogA11y from '../../hooks/useDialogA11y';

const Drawer = ({
  isOpen = false,
  onClose,
  title,
  children,
  position = 'right',
  size = 'md',
  showOverlay = true,
  closeOnOverlayClick = true,
  showCloseButton = true,
  className = '',
  ...props
}) => {
  const drawerRef = useClickOutside(() => {
    if (closeOnOverlayClick && isOpen) onClose?.();
  }, !isOpen || !closeOnOverlayClick);

  useDialogA11y({
    isOpen,
    containerRef: drawerRef,
    onClose,
  });

  const sizeClasses = {
    sm:   'w-64 sm:w-72',
    md:   'w-80 sm:w-96',
    lg:   'w-96 sm:w-[28rem]',
    xl:   'w-full sm:w-[32rem]',
    full: 'w-full',
  };

  const bottomSizeClasses = {
    sm: 'max-h-[60dvh]',
    md: 'max-h-[72dvh]',
    lg: 'max-h-[82dvh]',
    xl: 'max-h-[90dvh]',
    full: 'max-h-[92dvh]',
  };

  const slideClass = {
    left:  isOpen ? 'translate-x-0' : '-translate-x-full',
    right: isOpen ? 'translate-x-0' : 'translate-x-full',
    bottom: isOpen ? 'translate-y-0' : 'translate-y-full',
  };

  const panelPositionClass = {
    left: 'top-0 left-0 h-screen',
    right: 'top-0 right-0 h-screen',
    bottom: 'bottom-0 inset-x-0 w-full',
  };

  const panelSizeClass = position === 'bottom' ? bottomSizeClasses[size] : sizeClasses[size];

  const panelStyle = {
    background: 'var(--bg-card)',
    boxShadow: '0 0 48px rgba(0, 0, 0, 0.25)',
    ...(position === 'right'
      ? { borderLeft: '1px solid var(--border)' }
      : position === 'left'
        ? { borderRight: '1px solid var(--border)' }
        : {
            borderTop: '1px solid var(--border)',
            borderTopLeftRadius: '1.5rem',
            borderTopRightRadius: '1.5rem',
          }),
  };

  return (
    <>
      {/* Overlay */}
      {showOverlay && (
        <div
          className='fixed inset-0 transition-opacity duration-300 z-40 !mt-0'
          style={{
            background:    'rgba(0, 0, 0, 0.55)',
            backdropFilter: 'blur(4px)',
            opacity:       isOpen ? 1 : 0,
            pointerEvents: isOpen ? 'auto' : 'none',
          }}
          onClick={() => closeOnOverlayClick && onClose?.()}
          aria-hidden='true'
        />
      )}

      {/* Drawer panel */}
      <div
        ref={drawerRef}
        role='dialog'
        aria-modal='true'
        aria-labelledby={title ? 'drawer-title' : undefined}
        aria-label={title ? undefined : 'Drawer panel'}
        tabIndex={-1}
        className={`fixed ${panelPositionClass[position]} ${panelSizeClass} flex flex-col transform transition-transform duration-300 ease-in-out z-40 !mt-0 ${slideClass[position]} ${className}`}
        style={panelStyle}
        {...props}
      >
        {/* Header */}
        <div
          className='flex items-center justify-between px-5 py-4'
          style={{ borderBottom: '1px solid var(--border-subtle)' }}
        >
          {title && (
            <Text id='drawer-title' variant='title' className='!text-base'>
              {title}
            </Text>
          )}
          {showCloseButton && (
            <IconButton icon={X} onClick={onClose} tooltip='Close' className='ml-auto' variant='ghost' />
          )}
        </div>

        {/* Content */}
        <div className={`flex-1 overflow-y-auto px-5 py-4 thin-scrollbar ${position === 'bottom' ? 'pb-[calc(1rem+env(safe-area-inset-bottom,0px))]' : ''}`}>{children}</div>
      </div>
    </>
  );
};

const DrawerHeader = ({ children, className = '' }) => (
  <div className={`mb-4 ${className}`}>{children}</div>
);

const DrawerBody = ({ children, className = '' }) => (
  <div className={`space-y-4 ${className}`}>{children}</div>
);

const DrawerFooter = ({ children, className = '', align = 'right' }) => {
  const alignClass = {
    left:    'justify-start',
    center:  'justify-center',
    right:   'justify-end',
    between: 'justify-between',
  }[align];

  return (
    <div
      className={`flex gap-3 p-5 mt-0 ${alignClass} ${className}`}
      style={{ borderTop: '1px solid var(--border-subtle)' }}
    >
      {children}
    </div>
  );
};

Drawer.Header = DrawerHeader;
Drawer.Body   = DrawerBody;
Drawer.Footer = DrawerFooter;

export default Drawer;
